import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

// Create Dark Mode Context
const DarkModeContext = createContext();

// Custom hook to use Dark Mode Context
export const useDarkMode = () => {
    const context = useContext(DarkModeContext);
    if (!context) {
        throw new Error("useDarkMode must be used within a DarkModeProvider");
    }
    return context;
};

// Dark Mode Provider Component
// eslint-disable-next-line react/prop-types
export const DarkModeProvider = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        // Check localStorage for saved preference, default to light mode
        const savedMode = localStorage.getItem("darkMode");
        if (savedMode !== null) {
            return JSON.parse(savedMode);
        }
        // Default to light mode instead of system preference
        return false;
    });

    // Toggle dark mode
    const toggleDarkMode = () => {
        setIsDarkMode(prevMode => !prevMode);
    };

    // Apply dark mode to HTML element and save to localStorage
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
        // Save preference to localStorage
        localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
    }, [isDarkMode]);

    const value = useMemo(() => ({
        isDarkMode,
        toggleDarkMode
    }), [isDarkMode]);

    return (
        <DarkModeContext.Provider value={value}>
            {children}
        </DarkModeContext.Provider>
    );
};

export default DarkModeContext;
